import React, { useState, useRef, useEffect } from 'react';

const CustomDropdown = ({
                            options = [],
                            value,
                            onChange,
                            placeholder = "Select an option...",
                            className = "",
                            disabled = false
                        }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close dropdown on Escape key
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    const handleSelect = (option) => {
        onChange(option);
        setIsOpen(false);
    };

    const displayValue = (() => {
        if (!value) return placeholder;

        // Find the option that matches the current value
        const selectedOption = options.find(option => option.value === value);

        // Return the label if found, otherwise fall back to the value
        return selectedOption ? selectedOption.label : value;
    })();

    return (
        <div
            ref={dropdownRef}
            className={`custom-dropdown ${className} ${disabled ? 'disabled' : ''}`}
        >
            <div
                className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
                onClick={handleToggle}
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggle();
                    }
                }}
            >
                <span className="dropdown-value">{displayValue}</span>
                <svg
                    className={`dropdown-arrow ${isOpen ? 'rotated' : ''}`}
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                >
                    <path
                        d="M2 4l4 4 4-4"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </div>

            {isOpen && (
                <div className="dropdown-menu">
                    {options.length === 0 ? (
                        <div className="dropdown-item disabled">No options available</div>
                    ) : (
                        options.map((option, index) => (
                            <div
                                key={option.id || option.value || index}
                                // className={`dropdown-item ${value === option.value ? 'selected' : ''}`}
                                className={`dropdown-item`}
                                onClick={() => handleSelect(option)}
                            >
                                {option.label || option.name || option}
                            </div>
                        ))
                    )}
                </div>
            )}

            <style jsx>{`
                .custom-dropdown {
                    position: relative;
                    display: inline-block;
                    min-width: 200px;
                    width: 100%;
                }

                .custom-dropdown.disabled {
                    opacity: 0.9;
                    cursor: not-allowed;
                }

                .dropdown-trigger {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.5em 1em;
                    border-radius: 8px;
                    border: 2px solid rgba(5, 179, 130, 0.2);
                    background: rgba(255, 255, 255, 0.8);
                    backdrop-filter: blur(6px);
                    font-size: 1em;
                    font-weight: 500;
                    color: #047655;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
                    cursor: pointer;
                    transition: all 0.3s ease;
                    user-select: none;
                    width: 100%;
                    box-sizing: border-box;
                }

                .dropdown-trigger:hover:not(.custom-dropdown.disabled .dropdown-trigger) {
                    border-color: #05B382;
                }

                .dropdown-trigger:focus {
                    outline: none;
                    border-color: #05B382;
                    box-shadow: 0 0 0 3px rgba(5, 179, 130, 0.1);
                }

                .dropdown-trigger.open {
                    border-color: #05B382;
                    border-bottom-left-radius: 0;
                    border-bottom-right-radius: 0;
                }

                .dropdown-value {
                    flex: 1;
                    text-align: left;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    min-width: 0; /* This is important for text-overflow to work properly */
                }

                .dropdown-arrow {
                    margin-left: 8px;
                    transition: transform 0.3s ease;
                    color: #047655;
                    flex-shrink: 0; /* Prevent arrow from shrinking */
                }

                .dropdown-arrow.rotated {
                    transform: rotate(180deg);
                }

                .dropdown-menu {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    z-index: 1000;
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(10px);
                    border: 2px solid #05B382;
                    border-top: none;
                    border-radius: 0 0 8px 8px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
                    max-height: 200px;
                    overflow-y: auto;
                    width: 100%;
                    box-sizing: border-box;
                }

                .dropdown-item {
                    padding: 0.75em 1em;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    color: #047655;
                    font-weight: 500;
                    border-bottom: 1px solid rgba(5, 179, 130, 0.1);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    box-sizing: border-box;
                }

                .dropdown-item:last-child {
                    border-bottom: none;
                }

                .dropdown-item:hover:not(.disabled) {
                    background-color: rgba(5, 179, 130, 0.1);
                }

                .dropdown-item.selected {
                    background-color: rgba(5, 179, 130, 0.2);
                    font-weight: 600;
                }

                .dropdown-item.disabled {
                    cursor: not-allowed;
                    opacity: 0.5;
                    color: #999;
                }

                /* Scrollbar styling */
                .dropdown-menu::-webkit-scrollbar {
                    width: 6px;
                }

                .dropdown-menu::-webkit-scrollbar-track {
                    background: rgba(255, 255, 255, 0.1);
                }

                .dropdown-menu::-webkit-scrollbar-thumb {
                    background: rgba(5, 179, 130, 0.3);
                    border-radius: 3px;
                }

                .dropdown-menu::-webkit-scrollbar-thumb:hover {
                    background: rgba(5, 179, 130, 0.5);
                }
            `}</style>
        </div>
    );
};

export default CustomDropdown;